import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { AuditLog } from "@/api/types";
import { useAuth } from "@/auth/useAuth";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuditLog } from "@/features/audit-log/hooks/useAuditLog";

export function AuditLogPage() {
  const { t } = useTranslation("auditLog");
  const { user } = useAuth();
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const [resourceType, setResourceType] = useState("");
  const [page, setPage] = useState(0);
  const limit = 50;
  const query = useAuditLog({
    actor: actor || undefined,
    action: action || undefined,
    resource_type: resourceType || undefined,
    limit,
    offset: page * limit,
  });
  const [selected, setSelected] = useState<AuditLog | null>(null);

  if (!user?.is_admin) {
    return (
      <div className="rounded-2xl border border-dashed border-line bg-surface p-6 text-sm text-ink-500">
        {t("accessRestricted")}
      </div>
    );
  }

  async function exportFile(format: "csv" | "json") {
    const params = new URLSearchParams();
    if (actor) params.set("actor", actor);
    if (action) params.set("action", action);
    if (resourceType) params.set("resource_type", resourceType);
    params.set("format", format);
    const { tokenStorage } = await import("@/auth/tokenStorage");
    const { env } = await import("@/lib/env");
    const token = await tokenStorage.getToken();
    const resp = await fetch(
      `${env.API_BASE_URL}/audit-log/export?${params.toString()}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : undefined },
    );
    if (!resp.ok) return;
    const blob = await resp.blob();
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `audit-log.${format}`;
    link.click();
    window.URL.revokeObjectURL(link.href);
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("title")}
        description={t("subtitle")}
        actions={
          <>
            <Button size="sm" variant="outline" onClick={() => exportFile("csv")}>
              {t("exportCsv")}
            </Button>
            <Button size="sm" variant="outline" onClick={() => exportFile("json")}>
              {t("exportJson")}
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor="audit-actor" className="text-xs">
            {t("filters.actor")} (UUID)
          </Label>
          <Input
            id="audit-actor"
            value={actor}
            onChange={(e) => {
              setActor(e.target.value);
              setPage(0);
            }}
            placeholder="uuid"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="audit-action" className="text-xs">
            {t("filters.action")}
          </Label>
          <Input
            id="audit-action"
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setPage(0);
            }}
            placeholder="agent.installed"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="audit-resource-type" className="text-xs">
            {t("filters.resourceType")}
          </Label>
          <Input
            id="audit-resource-type"
            value={resourceType}
            onChange={(e) => {
              setResourceType(e.target.value);
              setPage(0);
            }}
            placeholder="agent / team / ..."
          />
        </div>
      </div>

      {query.isLoading ? <LoadingState /> : null}
      {query.isError ? <ErrorState error={query.error} /> : null}

      {query.data ? (
        <>
          <div className="shadow-card overflow-x-auto rounded-2xl border border-line bg-surface">
            <table className="w-full text-sm">
              <thead className="bg-canvas text-[10px] uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-4 py-2.5 text-left font-semibold">
                    {t("table.timestamp")}
                  </th>
                  <th className="px-4 py-2.5 text-left font-semibold">
                    {t("table.actor")}
                  </th>
                  <th className="px-4 py-2.5 text-left font-semibold">
                    {t("table.action")}
                  </th>
                  <th className="px-4 py-2.5 text-left font-semibold">
                    {t("table.resource")}
                  </th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line text-ink-900">
                {query.data.items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-3 text-center text-ink-500">
                      {t("empty")}
                    </td>
                  </tr>
                ) : null}
                {query.data.items.map((row) => (
                  <tr key={row.id} className="hover:bg-canvas/60">
                    <td className="px-4 py-2 font-mono text-xs text-ink-700">
                      {new Date(row.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {row.actor_email ? (
                        <div className="space-y-0.5">
                          <div>{row.actor_email}</div>
                          {row.actor_name ? (
                            <div className="text-ink-500">{row.actor_name}</div>
                          ) : null}
                        </div>
                      ) : row.actor_user_id ? (
                        <span className="font-mono text-ink-700">
                          {row.actor_user_id.slice(0, 8)}
                        </span>
                      ) : (
                        <span className="text-ink-500">{t("system")}</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <Badge variant="outline" title={row.action}>
                        {t(`actions.${row.action}`, {
                          defaultValue: row.action,
                        })}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 text-xs">
                      <div title={row.resource_type}>
                        {t(`resources.${row.resource_type}`, {
                          defaultValue: row.resource_type,
                        })}
                      </div>
                      {row.resource_name ? (
                        <div className="text-ink-500">{row.resource_name}</div>
                      ) : row.resource_id ? (
                        <div className="font-mono text-ink-500">
                          {row.resource_id.slice(0, 8)}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelected(row)}
                      >
                        {t("table.view")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span>
              {t("pageRange", {
                start: query.data.offset + 1,
                end: Math.min(
                  query.data.offset + query.data.items.length,
                  query.data.total,
                ),
                total: query.data.total,
              })}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                {t("previousPage")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage((p) => p + 1)}
                disabled={
                  query.data.offset + query.data.items.length >= query.data.total
                }
              >
                {t("nextPage")}
              </Button>
            </div>
          </div>
        </>
      ) : null}

      <Dialog
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("drawer.title")}</DialogTitle>
          </DialogHeader>
          {selected ? (
            <div className="space-y-3 text-xs">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" title={selected.action}>
                  {t(`actions.${selected.action}`, {
                    defaultValue: selected.action,
                  })}
                </Badge>
                <span className="font-mono">
                  {new Date(selected.created_at).toLocaleString()}
                </span>
                {selected.actor_email ? (
                  <span className="text-ink-500">
                    {t("by")} {selected.actor_email}
                  </span>
                ) : null}
                {selected.resource_name ? (
                  <span className="text-ink-500">
                    {t("on")}{" "}
                    {t(`resources.${selected.resource_type}`, {
                      defaultValue: selected.resource_type,
                    })}{" "}
                    "{selected.resource_name}"
                  </span>
                ) : null}
              </div>
              {/* Diff side-by-side: before | after. Sem lib externa --
                  apenas <pre> lado a lado pra reduzir surface area. */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <h4 className="font-semibold text-danger-700">
                    {t("drawer.before")}
                  </h4>
                  <pre className="mt-1 max-h-64 overflow-auto rounded-lg border border-danger-50 bg-danger-50 p-2 font-mono text-danger-700">
                    {selected.before
                      ? JSON.stringify(selected.before, null, 2)
                      : t("noBefore")}
                  </pre>
                </div>
                <div>
                  <h4 className="font-semibold text-success-700">
                    {t("drawer.after")}
                  </h4>
                  <pre className="mt-1 max-h-64 overflow-auto rounded-lg border border-success-50 bg-success-50 p-2 font-mono text-success-700">
                    {selected.after
                      ? JSON.stringify(selected.after, null, 2)
                      : t("noAfter")}
                  </pre>
                </div>
              </div>
              {selected.audit_metadata ? (
                <div>
                  <h4 className="font-semibold text-ink-900">
                    {t("drawer.metadata")}
                  </h4>
                  <pre className="mt-1 max-h-48 overflow-auto rounded-lg bg-canvas p-2 font-mono text-ink-700">
                    {JSON.stringify(selected.audit_metadata, null, 2)}
                  </pre>
                </div>
              ) : null}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
